/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { LinkPlugin as LexicalLinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import * as React from 'react';
import { useContext, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useNavigate } from 'react-router';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  ElementNode,
  LexicalNode,
} from 'lexical';
import { getCurrentSupportedLanguage } from '../../../../../../application/localization/localization.ts';
import { PageDesignerContext } from '../../../DesignerNewPage.tsx';
import { Language } from '../../../../../../services/api/api-client.types.ts';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import { $isLocalizationLinkNode } from '../../nodes/Localizable/LocalizationLinkNode.ts';
import { DefaultLanguageContext } from '../../../SinglePage.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $isLocalizationNode } from '../../nodes/Localizable/LocalizationNode.tsx';
import { $isTabItemNode } from '../../nodes/TabItemNode.ts';
import { CmsPageQuery } from 'services/api/api-client.ts';
import { useQueryClient } from '@tanstack/react-query';

const getTabItemParent = (node: ElementNode) => {
  const parent = node.getParent();

  if ($isTabItemNode(parent)) {
    return parent;
  }

  if (parent) {
    return getTabItemParent(parent);
  }

  return null;
};

export default function LinkPlugin(): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const domain = window.location.origin;
  const isEditable = useLexicalEditable();

  const i18n = useScopedTranslation('Page.PageDesigner');
  const currentOuterLanguage = getCurrentSupportedLanguage(i18n.i18n);

  const pageDesignerContext = useContext(PageDesignerContext);
  const defaultLanguageContext = useContext(DefaultLanguageContext);

  const data = queryClient.getQueryData(
    CmsPageQuery.getInaccessiblePublishedUrlsQueryKey(),
  ) as string[] | undefined;

  const currentLanguage = isEditable
    ? pageDesignerContext?.pageEditorLanguage
    : ((currentOuterLanguage.at(0)?.toUpperCase() +
        currentOuterLanguage.slice(1)) as Language);
  const defaultLanguage = isEditable
    ? pageDesignerContext?.defaultLanguage
    : defaultLanguageContext?.defaultLanguage;

  useEffect(() => {
    if (!editor) return;

    const removeNodeListener = editor.registerNodeTransform(
      LinkNode,
      (node) => {
        if (!node) return;

        let dom = editor.getElementByKey(node.__key);

        if (!dom) return;

        if (!isEditable) {
          const href = node.getURL() ?? '';
          const isFileUploadLink =
            href.includes('/api/files/') || href.includes('/api/share-point');

          const isTab = !!dom.parentElement?.parentElement
            ?.getAttribute('class')
            ?.includes('PlaygroundEditorTheme__tabItem');
          const parentClassname = dom.getAttribute('class') + ' ';
          const childStyles = dom.firstElementChild?.getAttribute('style');
          const childClassname = dom.firstElementChild?.getAttribute('class');
          const fileName = dom.firstElementChild?.textContent?.trim() ?? '';
          const pageName = window.location.pathname.split('/')[1];

          const isTabShouldBeRemoved =
            isTab && data && data.includes(node.getURL().replace('/', ''));

          if (isTabShouldBeRemoved) {
            const tabItem = getTabItemParent(node);
            if (tabItem) {
              tabItem.remove();
            }
          }

          if (isFileUploadLink)
            dom.onclick = (e) => {
              trackEvent({
                category: `CmsPage: ${pageName}`,
                action: 'Download file',
                name: `Filename: ${fileName}; href: ${href}`,
              });
            };

          const isUnderlined = !!childClassname?.includes('textUnderline');

          dom.className = (parentClassname ?? '') + (childClassname ?? '');
          dom.setAttribute('style', childStyles ?? '');

          if (isUnderlined && !dom.className.includes('textUnderline')) {
            dom.style.textDecoration = 'underline';
          }

          if (
            isFileUploadLink &&
            !dom.className.includes('file-link-container')
          ) {
            dom.className += ' file-link-container';
          }

          const loweredHref = href.toLowerCase();

          const isURLCurrent =
            window.location.href.toLowerCase() === loweredHref ||
            window.location.pathname.toLowerCase() === loweredHref;

          if (
            isURLCurrent &&
            isTab &&
            !dom.className.includes('active-tab') &&
            !pageDesignerContext?.isVersionsModeActive
          ) {
            dom.className += ' active-tab';
          }

          if (
            (href.includes(domain) || href.startsWith('/')) &&
            !isFileUploadLink
          ) {
            dom.onclick = (e) => {
              trackEvent({
                category: `CmsPage: ${pageName}`,
                action: 'Download file',
                name: `Filename: ${fileName} href: ${href}`,
              });
              e.preventDefault();
              e.stopPropagation();
              const endpoint = href.replace(domain, '');
              void navigate(endpoint);
            };
          }
        }
      },
    );

    return () => removeNodeListener();
  }, [editor, isEditable]);

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();

      const setLinkTranslations = (node: LexicalNode) => {
        if (
          $isLocalizationLinkNode(node) &&
          currentLanguage &&
          defaultLanguage
        ) {
          const link = node.getTranslation(currentLanguage);
          const defaultLink = node.getTranslation(defaultLanguage);

          if (node.getURL() !== link) {
            if (link !== null) {
              node.setURL(link);
            } else if (defaultLink !== null) {
              node.setURL(defaultLink);
            } else {
              node.setTranslations(currentLanguage, node.getURL());
            }
          }
        }

        if (node instanceof ElementNode) {
          node
            .getChildren()
            .forEach((child: LexicalNode) => setLinkTranslations(child));
        }
      };

      setLinkTranslations(root);
    });

    const unregister = editor.registerCommand(
      TOGGLE_LINK_COMMAND,
      (payload: string | null) => {
        const selection = $getSelection();

        const removeFormatSpan = (nodes: LexicalNode[]) => {
          const parent = nodes.at(0)?.getParent();

          if ($isLocalizationLinkNode(parent)) {
            const linkChildren = parent.getChildren();
            let childCounter = 0;

            for (let i = 0; i < linkChildren.length; i++) {
              if (!$isLocalizationNode(linkChildren.at(i))) {
                childCounter++;
              }

              if (childCounter > 1) {
                linkChildren.at(linkChildren.length - 1)?.remove();
                break;
              }
            }
          }
        };

        if ($isRangeSelection(selection)) {
          const nodes = selection.getNodes();

          if (!payload) {
            removeFormatSpan(nodes);
          } else {
            for (const node of nodes) {
              const parent = node.getParent();
              if ($isLocalizationLinkNode(parent) && currentLanguage) {
                parent.setTranslations(currentLanguage, payload);
                break;
              }
            }

            if (!payload.includes('/api/files/')) {
              removeFormatSpan(nodes);
            }
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      unregister();
    };
  }, [editor, currentLanguage, defaultLanguage]);

  return <LexicalLinkPlugin />;
}
